import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ONBOARDING_TOOLTIP_CLASS_NAME from '../../theme/constants/tooltip';

const LineClampText = ({ text, maxLines, sx, ...restProps }) => {
  const textRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  const updateIsClamped = useCallback(() => {
    const element = textRef.current;
    if (!element) return;

    const hasVerticalOverflow = element.scrollHeight > element.clientHeight + 1;
    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth + 1;
    setIsClamped(hasVerticalOverflow || hasHorizontalOverflow);
  }, []);

  useEffect(() => {
    updateIsClamped();
    const ResizeObserverConstructor = window.ResizeObserver;
    if (!textRef.current || !ResizeObserverConstructor) return undefined;
    const resizeObserver = new ResizeObserverConstructor(() => {
      updateIsClamped();
    });
    resizeObserver.observe(textRef.current);
    return () => resizeObserver.disconnect();
  }, [text, maxLines, updateIsClamped]);

  return (
    <Tooltip
      title={text}
      disableHoverListener={!isClamped}
      classes={{ tooltip: ONBOARDING_TOOLTIP_CLASS_NAME }}
    >
      <Typography
        ref={textRef}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          ...sx,
        }}
        {...restProps}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

LineClampText.propTypes = {
  text: PropTypes.string.isRequired,
  maxLines: PropTypes.number,
  sx: PropTypes.shape({}),
};

LineClampText.defaultProps = {
  maxLines: 1,
  sx: {},
};

export default LineClampText;
